import { useState, useRef, useEffect } from 'react';
// Constants
import { DEFAULT_EASE } from './constants';

// Tweens a single number towards its target, re-rendering on each frame.
// Where useAnimated drives attributes on one element, this drives a value that
// an entire render depends on - the zoom domain, say, from which every mark's
// position is computed. Retargeting mid-flight picks up from wherever the
// value had reached rather than jumping back to the start.
// Pass `from` to animate on mount: without it the value starts already at its
// target and only moves when the target later changes.
const useAnimatedNumber = (target, duration, from = null, ease = DEFAULT_EASE) => {

  const initial = from === null ? target : from;
  const [value, _set_value] = useState(initial);
  const current = useRef(initial);

  useEffect(() => {
    const from = current.current;
    if (from === target) return;
    if (duration <= 0) {
      current.current = target;
      _set_value(target);
      return;
    }

    let frame;
    const start_time = performance.now();
    const step = (now) => {
      const t = Math.min((now - start_time) / duration, 1);
      const next = from + (target - from) * ease(t);
      current.current = next;
      _set_value(next);
      if (t < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);

    return () => cancelAnimationFrame(frame);
  }, [target, duration, ease]);

  return value;
};

export default useAnimatedNumber;
